import React, { useState } from 'react';
import { AccessType } from '../../types';

interface TrialInfo {
  web_trial: boolean;
  ide_trial: boolean;
  security_trial: boolean;
  devops_trial: boolean;
  web_trial_expires: string | null;
  ide_trial_expires: string | null;
  security_trial_expires: string | null;
  devops_trial_expires: string | null;
}

interface TrialManagementProps {
  trials: TrialInfo;
  onChange: (trials: TrialInfo) => void;
  disabled?: boolean;
  level: 'user' | 'organization';
}

const TrialManagement: React.FC<TrialManagementProps> = ({
  trials,
  onChange,
  disabled = false,
  level
}) => {
  const [localTrials, setLocalTrials] = useState<TrialInfo>(trials);

  const accessTypes: { key: AccessType; label: string; color: string }[] = [
    { key: 'web', label: 'Web Trial', color: 'bg-blue-500' },
    { key: 'ide', label: 'IDE Trial', color: 'bg-green-500' },
    { key: 'security', label: 'Security Trial', color: 'bg-red-500' },
    { key: 'devops', label: 'DevOps Trial', color: 'bg-yellow-500' }
  ];

  const handleTrialToggle = (accessType: AccessType) => {
    if (disabled) return;

    const trialKey = `${accessType}_trial` as keyof TrialInfo;
    const expiresKey = `${accessType}_trial_expires` as keyof TrialInfo;
    
    const newTrials = {
      ...localTrials,
      [trialKey]: !localTrials[trialKey],
      [expiresKey]: !localTrials[trialKey] ? getDefaultExpiryDate() : null
    };

    setLocalTrials(newTrials);
    onChange(newTrials);
  };

  const handleExpiryChange = (accessType: AccessType, date: string) => {
    if (disabled) return;

    const expiresKey = `${accessType}_trial_expires` as keyof TrialInfo;
    const newTrials = {
      ...localTrials,
      [expiresKey]: date || null
    };

    setLocalTrials(newTrials);
    onChange(newTrials);
  };

  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7); // 7 days from now
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const isTrialActive = (accessType: AccessType) => {
    const trialKey = `${accessType}_trial` as keyof TrialInfo;
    const expiresKey = `${accessType}_trial_expires` as keyof TrialInfo;
    
    if (!localTrials[trialKey]) return false;
    
    const expiryDate = localTrials[expiresKey] as string | null;
    if (!expiryDate || typeof expiryDate !== 'string') return false;
    
    return new Date(expiryDate) > new Date();
  };

  const formatExpiryDate = (date: string | null) => {
    if (!date) return 'No expiry set';
    const expiryDate = new Date(date);
    const now = new Date();
    
    if (expiryDate < now) {
      return `Expired on ${expiryDate.toLocaleDateString()}`;
    }
    
    const daysLeft = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return `${daysLeft} days left (${expiryDate.toLocaleDateString()})`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Trial Management ({level === 'user' ? 'User Level' : 'Organization Level'})
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {level === 'user' 
            ? 'Grant trial access to specific services for this user'
            : 'Grant trial access to specific services for all users in this organization'
          }
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {accessTypes.map(({ key, label, color }) => {
          const trialKey = `${key}_trial` as keyof TrialInfo;
          const expiresKey = `${key}_trial_expires` as keyof TrialInfo;
          const isActive = isTrialActive(key);
          
          return (
            <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${color}`} />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {label}
                  </span>
                </div>
                
                <label className={`relative inline-flex items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localTrials[trialKey] as boolean}
                    onChange={() => handleTrialToggle(key)}
                    disabled={disabled}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${
                    localTrials[trialKey] ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                      localTrials[trialKey] ? 'translate-x-5' : 'translate-x-0.5'
                    } mt-0.5`} />
                  </div>
                </label>
              </div>

              {localTrials[trialKey] && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Trial Expires
                    </label>
                    <input
                      type="date"
                      value={localTrials[expiresKey] as string || ''}
                      onChange={(e) => handleExpiryChange(key, e.target.value)}
                      disabled={disabled}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  
                  <div className={`text-xs px-2 py-1 rounded ${
                    isActive 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {formatExpiryDate(localTrials[expiresKey] as string)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
          Trial Information
        </h4>
        <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
          <li>• Trials provide temporary access to premium features</li>
          <li>• {level === 'organization' ? 'Organization trials apply to all members' : 'User trials override organization settings'}</li>
          <li>• Expired trials automatically disable access</li>
          <li>• Super admins can extend or modify trials at any time</li>
        </ul>
      </div>
    </div>
  );
};

export default TrialManagement;
